import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Pencil, X, ArrowLeft, Eye, Heart, Star, ExternalLink, FileText, Video, Download as DownloadIcon, Mail, Sparkles, MessageSquare, Check, Users, BarChart3 } from "lucide-react";
import { API, api, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import RichTextEditor from "@/components/admin/RichTextEditor";

const emptyPost = {
  type: "article", section: "articles", title: "", slug: "", excerpt: "", body: "", cover_image: "",
  category: "", tags: [], author: "IWS Team", author_role: "Admin",
  video_url: "", video_thumbnail: "", video_duration: "",
  resource_url: "", resource_type: "", status: "draft", featured: false, faqs: [],
  seo_title: "", seo_description: "", seo_keywords: "", canonical_url: "", og_image: "", cta_type: "auto",
};

const TYPE_ICON = { article: FileText, video: Video, resource: DownloadIcon };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export default function HubManager() {
  const [mode, setMode] = useState("list");
  const [posts, setPosts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [comments, setComments] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [meta, setMeta] = useState({ sections: [], categories: {} });
  const [filter, setFilter] = useState("all");
  const token = typeof window !== "undefined" ? localStorage.getItem("iws_token") : "";

  const load = () => api.get("/admin/hub").then((r) => setPosts(r.data)).catch(() => {});
  const loadComments = () => api.get("/admin/comments").then((r) => setComments(r.data)).catch(() => {});
  const loadAuthors = () => api.get("/admin/authors").then((r) => setAuthors(r.data)).catch(() => {});
  const loadAnalytics = () => api.get("/admin/hub/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  useEffect(() => {
    load();
    loadComments();
    loadAuthors();
    loadAnalytics();
    api.get("/hub/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/admin/newsletter").then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  const modComment = async (id, status) => { await api.patch(`/admin/comments/${id}`, { status }); loadComments(); };
  const delComment = async (id) => { if (!window.confirm("Delete this comment?")) return; await api.delete(`/admin/comments/${id}`); loadComments(); };

  const openNew = () => { setEditing({ ...emptyPost }); setMode("edit"); };
  const openEdit = async (id) => {
    try { const r = await api.get(`/admin/hub/${id}`); setEditing({ ...emptyPost, ...r.data }); setMode("edit"); }
    catch { toast.error("Could not load post"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/hub/${id}`); toast.success("Post deleted"); load();
  };

  const filtered = useMemo(() => filter === "all" ? posts : posts.filter((p) => p.status === filter), [posts, filter]);

  if (mode === "edit") return <HubEditor post={editing} meta={meta} authors={authors} onCancel={() => { setMode("list"); setEditing(null); }} onSaved={() => { setMode("list"); setEditing(null); load(); loadAnalytics(); }} />;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="hub-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg bg-slate-100 p-1">
          {[["all", "All"], ["published", "Published"], ["scheduled", "Scheduled"], ["draft", "Drafts"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`hub-filter-${k}`} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === k ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
        <Button onClick={openNew} data-testid="hub-new-post" className="bg-emerald-brand hover:bg-emerald-dark gap-1"><Plus size={16} /> New Post</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400" data-testid="hub-empty">No posts yet. Click "New Post" to create your first article, video or resource.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr><th className="text-left px-5 py-3">Title</th><th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Section</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Views</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => { const TI = TYPE_ICON[p.type] || FileText; return (
                <tr key={p.id} data-testid={`hub-row-${i}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2 font-medium text-navy">{p.featured && <Star size={13} className="text-amber-500 fill-amber-500" />}{p.title}</div>
                    <div className="text-xs text-slate-400">{p.category}</div>
                  </td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-slate-600 capitalize"><TI size={14} /> {p.type}</span></td>
                  <td className="px-4 py-3 text-slate-600">{p.section}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-medium rounded-full px-2.5 py-1 ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : p.status === "scheduled" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span></td>
                  <td className="px-4 py-3 text-slate-600">{p.views || 0}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {p.status === "published" && <a href={`/hub/${p.slug}`} target="_blank" rel="noreferrer" title="View" className="p-2 text-slate-400 hover:text-navy" data-testid={`hub-view-${i}`}><ExternalLink size={15} /></a>}
                      <button onClick={() => openEdit(p.id)} title="Edit" className="p-2 text-slate-400 hover:text-emerald-brand" data-testid={`hub-edit-${i}`}><Pencil size={15} /></button>
                      <button onClick={() => remove(p.id)} title="Delete" className="p-2 text-slate-400 hover:text-red-500" data-testid={`hub-delete-${i}`}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ); })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="hub-newsletter-block">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-navy flex items-center gap-2"><Mail size={18} className="text-emerald-brand" /> Newsletter Subscribers <span className="text-sm font-normal text-slate-400">({subs.length})</span></h3>
          <Button size="sm" variant="outline" onClick={() => window.open(`${API}/admin/newsletter/export?auth=${token}`, "_blank")} data-testid="hub-newsletter-export" className="gap-1 border-navy text-navy hover:bg-navy hover:text-white"><DownloadIcon size={14} /> CSV</Button>
        </div>
        {subs.length === 0 ? <p className="text-sm text-slate-400">No subscribers yet.</p> : (
          <div className="flex flex-wrap gap-2">{subs.slice(0, 30).map((s) => <span key={s.id} className="text-xs bg-slate-100 rounded-full px-3 py-1 text-slate-600">{s.email}</span>)}</div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="hub-comments-block">
        <h3 className="font-display font-semibold text-navy flex items-center gap-2 mb-4"><MessageSquare size={18} className="text-emerald-brand" /> Comments <span className="text-sm font-normal text-slate-400">({comments.length})</span></h3>
        {comments.length === 0 ? <p className="text-sm text-slate-400">No comments yet. New comments appear here for your approval.</p> : (
          <div className="space-y-3">
            {comments.map((c, i) => (
              <div key={c.id} data-testid={`hub-comment-${i}`} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-navy flex items-center gap-2">{c.name}
                      <span className={`text-xs rounded-full px-2 py-0.5 ${c.status === "approved" ? "bg-emerald-100 text-emerald-700" : c.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{c.status}</span>
                    </p>
                    <p className="text-xs text-slate-400 truncate">on "{c.post_title}"</p>
                    <p className="mt-1 text-sm text-slate-600">{c.body}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {c.status !== "approved" && <button onClick={() => modComment(c.id, "approved")} title="Approve" data-testid={`hub-comment-approve-${i}`} className="p-2 text-slate-400 hover:text-emerald-brand"><Check size={15} /></button>}
                    {c.status !== "rejected" && <button onClick={() => modComment(c.id, "rejected")} title="Reject" data-testid={`hub-comment-reject-${i}`} className="p-2 text-slate-400 hover:text-amber-600"><X size={15} /></button>}
                    <button onClick={() => delComment(c.id)} title="Delete" data-testid={`hub-comment-delete-${i}`} className="p-2 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AuthorsPanel authors={authors} reload={loadAuthors} />
      <AnalyticsPanel data={analytics} />
    </div>
  );
}

function HubEditor({ post, meta, authors = [], onCancel, onSaved }) {
  const [p, setP] = useState(post);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!p.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...p, tags: Array.isArray(p.tags) ? p.tags : String(p.tags).split(",").map((t) => t.trim()).filter(Boolean) };
      if (p.id) await api.put(`/admin/hub/${p.id}`, payload);
      else await api.post("/admin/hub", payload);
      toast.success("Post saved & published"); onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const addFaq = () => set("faqs", [...(p.faqs || []), { q: "", a: "" }]);
  const updFaq = (i, k, v) => set("faqs", p.faqs.map((f, idx) => idx === i ? { ...f, [k]: v } : f));
  const delFaq = (i) => set("faqs", p.faqs.filter((_, idx) => idx !== i));

  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const runAi = async () => {
    if (!p.title.trim() && !p.body.trim() && !p.excerpt.trim()) { toast.error("Add a title or some content first"); return; }
    setAiLoading(true);
    try {
      const r = await api.post("/admin/hub/ai-seo", { title: p.title, body: p.body, excerpt: p.excerpt });
      setAi(r.data);
      setP((prev) => ({ ...prev,
        seo_title: prev.seo_title || r.data.seo_title || "",
        seo_description: prev.seo_description || r.data.seo_description || "",
        seo_keywords: prev.seo_keywords || r.data.seo_keywords || "" }));
      toast.success("AI suggestions ready");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "AI assistant failed"); }
    finally { setAiLoading(false); }
  };
  const applyAi = () => setP((prev) => ({ ...prev, seo_title: ai.seo_title || prev.seo_title, seo_description: ai.seo_description || prev.seo_description, seo_keywords: ai.seo_keywords || prev.seo_keywords }));

  const tagsStr = Array.isArray(p.tags) ? p.tags.join(", ") : p.tags;
  const inp = "mt-1.5";

  return (
    <div className="space-y-6 max-w-4xl" data-testid="hub-editor">
      <div className="flex items-center justify-between">
        <button onClick={onCancel} data-testid="hub-editor-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy"><ArrowLeft size={16} /> Back to posts</button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="gap-1"><X size={15} /> Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="hub-save" className="bg-emerald-brand hover:bg-emerald-dark gap-1"><Save size={15} /> {saving ? "Saving…" : "Save & Publish"}</Button>
        </div>
      </div>

      <Section title="Content Type">
        <div className="flex gap-2">
          {[["article", "Article", FileText], ["video", "Video", Video], ["resource", "Resource", DownloadIcon]].map(([k, l, I]) => (
            <button key={k} onClick={() => set("type", k)} data-testid={`hub-type-${k}`} className={`flex-1 flex flex-col items-center gap-1.5 rounded-xl border-2 py-4 transition-colors ${p.type === k ? "border-emerald-brand bg-emerald-brand/5 text-emerald-brand" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
              <I size={22} /><span className="text-sm font-medium">{l}</span>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Basics">
        <div><Label>Title *</Label><Input data-testid="hub-title" className={inp} value={p.title} onChange={(e) => set("title", e.target.value)} /></div>
        <div><Label>Slug (URL) — leave blank to auto-generate</Label><Input data-testid="hub-slug" className={inp} placeholder="auto from title" value={p.slug} onChange={(e) => set("slug", e.target.value)} /></div>
        <div><Label>Excerpt / short summary</Label><Textarea data-testid="hub-excerpt" className={inp} rows={2} value={p.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></div>
        <div><Label>Cover image URL</Label><Input data-testid="hub-cover" className={inp} placeholder="https://…" value={p.cover_image} onChange={(e) => set("cover_image", e.target.value)} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Section</Label>
            <select data-testid="hub-section" className="mt-1.5 h-10 w-full rounded-md border border-slate-200 text-sm px-3 bg-white" value={p.section} onChange={(e) => set("section", e.target.value)}>
              {(meta.sections || []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div><Label>Category</Label>
            <select data-testid="hub-category" className="mt-1.5 h-10 w-full rounded-md border border-slate-200 text-sm px-3 bg-white" value={p.category} onChange={(e) => set("category", e.target.value)}>
              <option value="">— Select —</option>
              {Object.entries(meta.categories || {}).map(([grp, cats]) => (
                <optgroup key={grp} label={grp}>{cats.map((c) => <option key={c} value={c}>{c}</option>)}</optgroup>
              ))}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Tags (comma separated)</Label><Input data-testid="hub-tags" className={inp} value={tagsStr} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} /></div>
          <div><Label>Author</Label><Input data-testid="hub-author" list="hub-authors-list" className={inp} value={p.author} onChange={(e) => set("author", e.target.value)} /><datalist id="hub-authors-list">{authors.map((a) => <option key={a.id} value={a.name} />)}</datalist></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Author role</Label>
            <select className="mt-1.5 h-10 w-full rounded-md border border-slate-200 text-sm px-3 bg-white" value={p.author_role} onChange={(e) => set("author_role", e.target.value)}>
              {["Admin", "HR Team", "Guest Author"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div><Label>Status</Label>
            <select data-testid="hub-status" className="mt-1.5 h-10 w-full rounded-md border border-slate-200 text-sm px-3 bg-white" value={p.status} onChange={(e) => set("status", e.target.value)}>
              <option value="draft">Draft</option><option value="scheduled">Scheduled</option><option value="published">Published</option>
            </select>
          </div>
        </div>
        {p.status === "scheduled" && (
          <div><Label>Publish date &amp; time</Label>
            <Input type="datetime-local" data-testid="hub-scheduled-at" className={inp} value={toLocalInput(p.scheduled_at)} onChange={(e) => set("scheduled_at", e.target.value ? new Date(e.target.value).toISOString() : "")} />
            <p className="text-xs text-slate-400 mt-1">The post auto-publishes at this time and emails subscribers (if enabled).</p>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={!!p.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="hub-featured" /> Feature / pin this post</label>
        <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" className="h-4 w-4 accent-emerald-600" checked={p.notify_subscribers !== false} onChange={(e) => set("notify_subscribers", e.target.checked)} data-testid="hub-notify" /> Email newsletter subscribers when this goes live</label>
      </Section>

      {p.type === "video" && (
        <Section title="Video">
          <div><Label>Video URL (YouTube or Vimeo)</Label><Input data-testid="hub-video-url" className={inp} placeholder="https://youtube.com/watch?v=…" value={p.video_url} onChange={(e) => set("video_url", e.target.value)} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Thumbnail URL</Label><Input className={inp} placeholder="https://…" value={p.video_thumbnail} onChange={(e) => set("video_thumbnail", e.target.value)} /></div>
            <div><Label>Duration</Label><Input className={inp} placeholder="e.g. 5:24" value={p.video_duration} onChange={(e) => set("video_duration", e.target.value)} /></div>
          </div>
        </Section>
      )}

      {p.type === "resource" && (
        <Section title="Downloadable Resource">
          <div><Label>File URL (PDF, DOCX, XLSX, PPT, ZIP)</Label><Input data-testid="hub-resource-url" className={inp} placeholder="https://…/template.pdf" value={p.resource_url} onChange={(e) => set("resource_url", e.target.value)} /></div>
          <div><Label>File type label</Label><Input className={inp} placeholder="e.g. PDF" value={p.resource_type} onChange={(e) => set("resource_type", e.target.value)} /></div>
        </Section>
      )}

      {p.type === "article" && (
        <Section title="Article Body">
          <RichTextEditor value={p.body} onChange={(v) => set("body", v)} testid="hub-rte" />
        </Section>
      )}

      <Section title="FAQs (optional, boosts SEO)">
        {(p.faqs || []).map((f, i) => (
          <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`hub-faq-${i}`}>
            <div className="flex gap-2"><Input placeholder="Question" value={f.q} onChange={(e) => updFaq(i, "q", e.target.value)} data-testid={`hub-faq-q-${i}`} /><button onClick={() => delFaq(i)} className="text-slate-400 hover:text-red-500 shrink-0"><Trash2 size={16} /></button></div>
            <Textarea placeholder="Answer" rows={2} value={f.a} onChange={(e) => updFaq(i, "a", e.target.value)} data-testid={`hub-faq-a-${i}`} />
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addFaq} data-testid="hub-add-faq" className="gap-1 border-emerald-brand text-emerald-brand hover:bg-emerald-brand hover:text-white"><Plus size={14} /> Add FAQ</Button>
      </Section>

      <Section title="Lead-Generation CTA">
        <Label>Show this call-to-action after the post</Label>
        <select data-testid="hub-cta" className="mt-1.5 h-10 w-full rounded-md border border-slate-200 text-sm px-3 bg-white" value={p.cta_type} onChange={(e) => set("cta_type", e.target.value)}>
          <option value="auto">Auto (based on section)</option>
          <option value="employer">Employer — Request Talent</option>
          <option value="candidate">Candidate — Upload Resume</option>
          <option value="hr">HR — Join Community</option>
          <option value="company">Company — Book Consultation</option>
          <option value="none">None</option>
        </select>
      </Section>

      <Section title="SEO">
        <div className="flex items-center justify-between gap-3 -mt-1">
          <p className="text-sm text-slate-500">Let AI draft your SEO title, description, keywords and score your post.</p>
          <Button type="button" size="sm" onClick={runAi} disabled={aiLoading} data-testid="hub-ai-seo" className="bg-navy hover:bg-navy/90 gap-1.5 shrink-0"><Sparkles size={15} /> {aiLoading ? "Thinking…" : "AI Suggest"}</Button>
        </div>
        {ai && (
          <div className="rounded-xl border border-emerald-brand/30 bg-emerald-brand/5 p-4 space-y-3" data-testid="hub-ai-result">
            <div className="flex flex-wrap gap-3">
              <ScoreDial label="SEO score" value={ai.seo_score} />
              <ScoreDial label="Readability" value={ai.readability_score} />
              <button type="button" onClick={applyAi} data-testid="hub-ai-apply" className="ml-auto self-center text-sm font-semibold text-emerald-brand hover:underline">Apply suggestions →</button>
            </div>
            {(ai.tips || []).length > 0 && (
              <ul className="text-sm text-slate-600 list-disc ml-5 space-y-1">{ai.tips.map((t, i) => <li key={i}>{t}</li>)}</ul>
            )}
          </div>
        )}
        <div><Label>SEO title</Label><Input data-testid="hub-seo-title" className={inp} placeholder="Defaults to post title" value={p.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></div>
        <div><Label>Meta description</Label><Textarea className={inp} rows={2} value={p.seo_description} onChange={(e) => set("seo_description", e.target.value)} /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Keywords</Label><Input className={inp} value={p.seo_keywords} onChange={(e) => set("seo_keywords", e.target.value)} /></div>
          <div><Label>Canonical URL</Label><Input className={inp} placeholder="https://…" value={p.canonical_url} onChange={(e) => set("canonical_url", e.target.value)} /></div>
        </div>
        <div><Label>Open Graph image URL</Label><Input className={inp} placeholder="https://… (1200×630)" value={p.og_image} onChange={(e) => set("og_image", e.target.value)} /></div>
      </Section>

      <div className="flex justify-end pb-8">
        <Button onClick={save} disabled={saving} data-testid="hub-save-bottom" className="bg-emerald-brand hover:bg-emerald-dark gap-1"><Save size={15} /> {saving ? "Saving…" : "Save & Publish"}</Button>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
    <h3 className="font-display font-semibold text-navy">{title}</h3>
    {children}
  </div>
);

function ScoreDial({ label, value }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const color = v >= 75 ? "text-emerald-brand" : v >= 50 ? "text-amber-500" : "text-red-500";
  return (
    <div className="flex items-center gap-2" data-testid={`hub-score-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <span className={`font-display font-bold text-2xl ${color}`}>{v}</span>
      <span className="text-xs text-slate-500 leading-tight">{label}<br /><span className="text-slate-400">/ 100</span></span>
    </div>
  );
}

function AuthorsPanel({ authors, reload }) {
  const empty = { name: "", role: "", photo: "", bio: "", linkedin: "" };
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    if (!form.name.trim()) { toast.error("Author name is required"); return; }
    try {
      if (editId) await api.put(`/admin/authors/${editId}`, form);
      else await api.post("/admin/authors", form);
      toast.success("Author saved"); setForm(empty); setEditId(null); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
  };
  const edit = (a) => { setForm({ name: a.name, role: a.role || "", photo: a.photo || "", bio: a.bio || "", linkedin: a.linkedin || "" }); setEditId(a.id); };
  const del = async (id) => { if (!window.confirm("Delete this author?")) return; await api.delete(`/admin/authors/${id}`); if (editId === id) { setForm(empty); setEditId(null); } reload(); };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="hub-authors-block">
      <h3 className="font-display font-semibold text-navy flex items-center gap-2 mb-4"><Users size={18} className="text-emerald-brand" /> Authors <span className="text-sm font-normal text-slate-400">({authors.length})</span></h3>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-3">
          {authors.length === 0 ? <p className="text-sm text-slate-400">No authors yet. Add one so articles show a byline.</p> : authors.map((a, i) => (
            <div key={a.id} data-testid={`hub-author-${i}`} className="flex items-center gap-3 border border-slate-200 rounded-lg p-2.5">
              {a.photo ? <img src={a.photo} alt={a.name} className="h-10 w-10 rounded-full object-cover" /> : <span className="h-10 w-10 rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center font-semibold">{a.name[0]}</span>}
              <div className="min-w-0 flex-1"><p className="text-sm font-medium text-navy truncate">{a.name}</p><p className="text-xs text-slate-400 truncate">{a.role}</p></div>
              <button onClick={() => edit(a)} className="p-1.5 text-slate-400 hover:text-emerald-brand" data-testid={`hub-author-edit-${i}`}><Pencil size={14} /></button>
              <button onClick={() => del(a.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`hub-author-delete-${i}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <div className="space-y-2 sm:border-l border-slate-100 sm:pl-5">
          <p className="text-sm font-medium text-navy">{editId ? "Edit author" : "Add author"}</p>
          <Input placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="hub-author-name" />
          <Input placeholder="Role (e.g. HR Team)" value={form.role} onChange={(e) => set("role", e.target.value)} />
          <Input placeholder="Photo URL" value={form.photo} onChange={(e) => set("photo", e.target.value)} data-testid="hub-author-photo" />
          <Input placeholder="LinkedIn URL" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} />
          <Textarea rows={2} placeholder="Short bio" value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="hub-author-bio" />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} data-testid="hub-author-save" className="bg-emerald-brand hover:bg-emerald-dark gap-1"><Save size={14} /> Save</Button>
            {editId && <Button size="sm" variant="outline" onClick={() => { setForm(empty); setEditId(null); }}>Cancel</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsPanel({ data }) {
  if (!data) return null;
  const t = data.totals || {};
  const kpis = [["Views", t.views], ["Likes", t.likes], ["Comments", t.comments], ["Downloads", t.downloads], ["Video Plays", t.plays]];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="hub-analytics-block">
      <h3 className="font-display font-semibold text-navy flex items-center gap-2 mb-4"><BarChart3 size={18} className="text-emerald-brand" /> Content Analytics</h3>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
        {kpis.map(([l, v]) => (<div key={l} className="bg-slate-50 rounded-lg p-3 text-center"><div className="font-display font-bold text-2xl text-navy">{v || 0}</div><div className="text-xs text-slate-500">{l}</div></div>))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-slate-500 text-xs uppercase"><tr><th className="text-left py-2">Post</th><th className="text-right px-2">Views</th><th className="text-right px-2">Likes</th><th className="text-right px-2">Comments</th><th className="text-right px-2">Downloads</th><th className="text-right px-2">Plays</th><th className="text-right px-2">Read</th></tr></thead>
          <tbody>
            {(data.posts || []).map((p, i) => (
              <tr key={p.id} data-testid={`hub-analytics-row-${i}`} className="border-t border-slate-100"><td className="py-2 pr-2 text-navy font-medium truncate max-w-[220px]">{p.title}</td><td className="text-right px-2">{p.views || 0}</td><td className="text-right px-2">{p.likes || 0}</td><td className="text-right px-2">{p.comments || 0}</td><td className="text-right px-2">{p.type === "resource" ? (p.downloads || 0) : "—"}</td><td className="text-right px-2">{p.type === "video" ? (p.plays || 0) : "—"}</td><td className="text-right px-2">{p.reading_time || 1}m</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
